import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { Input, List, Modal, Tooltip } from 'antd'
import styled from 'styled-components'
import _ from 'lodash'
import { colours } from '@qflow/theme'

import { ColourButton, icons } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { AlertRecipients } from './AlertRecipients'
import {
  AdditionalText,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { EnableAlertRuleSwitch } from './EnableSwitch'

// Helper function to normalize and parse supplier input
const parseSuppliers = (input: string): string[] => {
  return input
    .replace(/;/g, '\n')
    .split('\n')
    .map(part => part.trim())
    .map(part => part.replace(/[,.\s]+$/, '')) // Remove trailing punctuation and whitespace
    .filter(part => part !== '')
}

// Helper function to sort suppliers alphabetically
const sortSuppliers = (suppliers: string[]): string[] => {
  return [...suppliers].sort((a, b) => a.localeCompare(b))
}

export const ProjectSuppliers = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}) => {
  const dispatch = useDispatch()
  const [projectSuppliersListInput, setProjectSuppliersListInput] = useState('')
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false)

  // Memoize parsed suppliers and input validation
  const parsedSuppliers = useMemo(
    () => parseSuppliers(projectSuppliersListInput),
    [projectSuppliersListInput]
  )

  const isInputEmpty = useMemo(
    () => projectSuppliersListInput.trim() === '',
    [projectSuppliersListInput]
  )

  const addValueToAlertRules = (value: string) => {
    const newSuppliers = parseSuppliers(value)
    const updatedAlertRules = _.cloneDeep(alertRules)
    const currentSuppliers = updatedAlertRules[alertKey].projectSuppliers

    // Deduplicate within the new suppliers first
    const uniqueNewSuppliers = [...new Set(newSuppliers)]

    // Add only new suppliers (avoid duplicates with existing suppliers)
    const suppliersToAdd = uniqueNewSuppliers.filter(
      supplier => !currentSuppliers.includes(supplier)
    )

    if (suppliersToAdd.length > 0) {
      updatedAlertRules[alertKey].projectSuppliers = sortSuppliers([
        ...currentSuppliers,
        ...suppliersToAdd
      ])
      setAlertRules(updatedAlertRules)

      dispatch(
        trackMixpanel('frontend_alert_rule_project_supplier_add', {
          project_id: projectId,
          suppliers_added_count: suppliersToAdd.length
        })
      )
    }

    setProjectSuppliersListInput('')
  }

  const replaceValueInAlertRules = (value: string) => {
    const parsedSuppliers = parseSuppliers(value)
    // Deduplicate within the new suppliers
    const newSuppliers = [...new Set(parsedSuppliers)]
    const updatedAlertRules = _.cloneDeep(alertRules)
    const previousSupplierCount =
      updatedAlertRules[alertKey].projectSuppliers.length

    updatedAlertRules[alertKey].projectSuppliers = sortSuppliers(newSuppliers)
    setAlertRules(updatedAlertRules)

    dispatch(
      trackMixpanel('frontend_alert_rule_project_supplier_replace', {
        project_id: projectId,
        previous_supplier_count: previousSupplierCount,
        new_supplier_count: newSuppliers.length
      })
    )

    setProjectSuppliersListInput('')
    setIsReplaceModalOpen(false)
  }

  const handleReplaceClick = () => {
    setIsReplaceModalOpen(true)
  }

  const handleReplaceConfirm = () => {
    replaceValueInAlertRules(projectSuppliersListInput)
  }

  const handleReplaceCancel = () => {
    setIsReplaceModalOpen(false)
  }

  const handleRemoveSupplier = useCallback(
    (supplier: string) => {
      const updatedAlertRules = _.cloneDeep(alertRules)
      updatedAlertRules[alertKey].projectSuppliers = updatedAlertRules[
        alertKey
      ].projectSuppliers.filter(s => s !== supplier)
      setAlertRules(updatedAlertRules)
    },
    [alertRules, alertKey, setAlertRules]
  )

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Project Suppliers</Description>
          <AdditionalText>
            Get notified if a delivery is from a supplier not listed as a
            project supplier.
          </AdditionalText>
        </TextArea>
        <SettingArea>
          <EnableAlertRuleSwitch
            alertKey={alertKey}
            alertRules={alertRules}
            setAlertRules={setAlertRules}
            cannotEnable={cannotEnable}
            ariaLabel="Enable Project Suppliers"
          />
          <ProjectSuppliersWrapper>
            <ProjectSuppliersInputWrapper>
              <Input.TextArea
                id="project-suppliers-input"
                allowClear
                rows={4}
                style={{ width: '404px' }}
                value={projectSuppliersListInput}
                onChange={e => setProjectSuppliersListInput(e.target.value)}
                placeholder="Enter supplier name"
              />
              <ButtonsWrapper>
                <Tooltip
                  title={
                    isInputEmpty
                      ? 'Enter at least one supplier to add to the Project Suppliers list'
                      : ''
                  }
                >
                  <ColourButton.Action
                    disabled={isInputEmpty}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      addValueToAlertRules(projectSuppliersListInput)
                    }}
                  >
                    Add
                  </ColourButton.Action>
                </Tooltip>
                <Tooltip
                  title={
                    isInputEmpty
                      ? 'Enter at least one supplier to replace the Project Suppliers list'
                      : 'Replace the entire Project Suppliers list with the entered suppliers'
                  }
                >
                  <ColourButton.Basic
                    disabled={isInputEmpty}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleReplaceClick()
                    }}
                  >
                    Replace
                  </ColourButton.Basic>
                </Tooltip>
              </ButtonsWrapper>
            </ProjectSuppliersInputWrapper>
            <AdditionalText style={{ paddingLeft: '0.125rem' }}>
              <div>
                Add multiple suppliers at once by separating names with a new
                line or semicolon.
              </div>
            </AdditionalText>
            <List
              size="small"
              bordered
              dataSource={alertRules[alertKey].projectSuppliers}
              locale={{
                emptyText: (
                  <div>
                    {!alertRules[alertKey].isEnabled ? (
                      'No project suppliers in list.'
                    ) : (
                      <span style={{ color: colours.RED_DARKMODE }}>
                        One or more suppliers required
                      </span>
                    )}
                  </div>
                )
              }}
              style={{
                width: '442px',
                marginTop: '0.5rem',
                backgroundColor: colours.WHITE,
                maxHeight: '13rem',
                overflowY: 'auto'
              }}
              renderItem={(supplier: string) => (
                <List.Item style={{ padding: '0' }}>
                  <ProjectSupplierWrapper>
                    {supplier}
                    <Tooltip title="Remove supplier">
                      <ColourButton.BasicBorderless
                        name={`remove-supplier-${supplier.replace(/\s+/g, '-').toLowerCase()}`}
                        aria-label={'Remove supplier'}
                        style={{
                          padding: '0.25rem 9px',
                          borderRadius: '50%',
                          color: colours.RED_DARKMODE
                        }}
                        onClick={() => handleRemoveSupplier(supplier)}
                      >
                        <icons.TrashCan colour="currentColor" />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  </ProjectSupplierWrapper>
                </List.Item>
              )}
            />
          </ProjectSuppliersWrapper>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipients
        projectId={projectId}
        alertKey={alertKey}
        alertRules={alertRules}
        setAlertRules={setAlertRules}
      />
      <Modal
        title="Replace Project Suppliers?"
        open={isReplaceModalOpen}
        onCancel={handleReplaceCancel}
        onOk={handleReplaceConfirm}
        footer={[
          <ColourButton.Basic
            key="cancel"
            type="button"
            onClick={handleReplaceCancel}
            style={{ display: 'inline-block', marginRight: '.75rem' }}
          >
            Cancel
          </ColourButton.Basic>,
          <ColourButton.Negative
            key="replace"
            type="button"
            onClick={handleReplaceConfirm}
            style={{ display: 'inline-block' }}
          >
            Replace
          </ColourButton.Negative>
        ]}
      >
        <p>
          This will replace the entire existing list of{' '}
          {alertRules[alertKey].projectSuppliers.length} supplier
          {alertRules[alertKey].projectSuppliers.length !== 1
            ? 's'
            : ''} with {parsedSuppliers.length} new supplier
          {parsedSuppliers.length !== 1 ? 's' : ''}.
        </p>
        <p style={{ marginTop: '1rem', fontWeight: 'bold' }}>
          This action cannot be undone. Are you sure you want to continue?
        </p>
      </Modal>
    </>
  )
}

export const ProjectSuppliersInputWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ProjectSuppliersWrapper = styled.div`
  margin-top: 0.25rem;
  min-width: 442px;
`

const ProjectSupplierWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  padding: 0.25rem 0.25rem 0.25rem 0.625rem;
`
